import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * The portal's button.
 *
 * One component behind every clickable action, so a button is the same object
 * everywhere: same heights, same radius, same hover lift, same focus ring. The
 * rules live in `styles/_button.scss`; this file is the API over them.
 *
 *   <Button variant="primary" icon={<i className="fa fa-file-export" />}>
 *     Export
 *   </Button>
 *
 * Choosing a variant:
 *
 *   primary    the one action a screen exists for. At most one per view -
 *              a row of filled buttons is a row of competing calls to action.
 *   secondary  the default. Every ordinary action is this.
 *   ghost      dense clusters - toolbars, card corners, list rows - where a
 *              border per button would out-weigh the content.
 *   link       reads as a link, for jumps that sit in running content. Drops
 *              the button geometry so it can sit on a text baseline.
 *   success    completing something: approve, submit, mark ready.
 *   danger     destructive and irreversible: delete, reject, discard.
 *   *-soft     the same intent as an outline, for bars that hold several of
 *              them at once and would shout if every one were filled.
 *   on-dark    anything sitting on the indigo shell.
 *
 * `className` still flows through, so a one-off can add layout (including
 * Tailwind utilities) without needing a new variant.
 */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'success'
  | 'success-soft'
  | 'danger'
  | 'danger-soft'
  | 'on-dark';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading glyph. Pass an `<i>`, an `<Icon>` or an SVG. */
  icon?: ReactNode;
  /** Trailing glyph - a caret, a count, a chevron. */
  trailingIcon?: ReactNode;
  /**
   * In flight: a 14px spinner takes the glyph's place, the label stays (or
   * becomes `loadingLabel`), and the button stops taking presses.
   */
  loading?: boolean;
  /**
   * The label while `loading`, as a present participle - "Releasing",
   * "Saving 2". Shown with a trailing ellipsis. Defaults to the label itself.
   */
  loadingLabel?: ReactNode;
  /** Stretches to the width of its container. */
  block?: boolean;
  children?: ReactNode;
}

function classes(
  variant: ButtonVariant,
  size: ButtonSize,
  extra: Array<string | false | null | undefined>
): string {
  return [
    'cw-btn',
    `cw-btn--${variant}`,
    size !== 'md' && `cw-btn--${size}`,
    ...extra
  ]
    .filter(Boolean)
    .join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon,
    trailingIcon,
    loading = false,
    loadingLabel,
    block = false,
    disabled,
    className,
    type = 'button',
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes(variant, size, [
        block && 'cw-btn--block',
        loading && 'cw-btn--loading',
        className
      ])}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="cw-btn__spinner" aria-hidden="true" /> : icon}
      {loading && loadingLabel ? <>{loadingLabel}…</> : children}
      {!loading && trailingIcon}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'trailingIcon' | 'block' | 'loadingLabel'> {
  /** Required: with no visible label this is the button's only accessible name. */
  label: string;
  children: ReactNode;
}

/**
 * A square, label-less button.
 *
 * `label` is required rather than optional because the glyph carries no
 * accessible name of its own - an icon button without one is announced as
 * "button" and nothing else.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    variant = 'ghost',
    size = 'md',
    loading = false,
    disabled,
    className,
    type = 'button',
    label,
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes(variant, size, [
        'cw-btn--icon',
        // Also carries the touch-target floor in styles/_responsive.scss, which
        // widens icon-only controls to 40px on coarse pointers.
        'cw-icon-button',
        loading && 'cw-btn--loading',
        className
      ])}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={label}
      title={rest.title ?? label}
      {...rest}
    >
      {loading ? <span className="cw-btn__spinner" aria-hidden="true" /> : children}
    </button>
  );
});

export interface ButtonGroupProps {
  /** `segmented` butts the buttons together; `row` spaces them evenly. */
  variant?: 'segmented' | 'row';
  className?: string;
  'aria-label'?: string;
  children: ReactNode;
}

/** A related set of buttons: segmented as one control, or spaced as a row. */
export function ButtonGroup({
  variant = 'segmented',
  className,
  children,
  ...rest
}: ButtonGroupProps) {
  return (
    <div
      className={[variant === 'segmented' ? 'cw-btn-group' : 'cw-btn-row', className]
        .filter(Boolean)
        .join(' ')}
      role="group"
      {...rest}
    >
      {children}
    </div>
  );
}

export default Button;
